// Phase 6 — Track D T-D4. usePayments hook.
//
// The shared ApiClient currently exposes only a `paid` filter on the wire;
// the date-range filter is applied client-side here (the architect's plan
// section 3 D-D4 / shared/api/client.ts:256-262 documents this — the
// payload is small and the backend gains a server-side range filter in a
// later patch). The hook signature still takes from/to so callers and the
// URL-sync work the same way today and tomorrow.

import * as React from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PaymentTransaction, PaymentTransactionList } from '@dailydeck/shared/types';
import { apiClient } from '../app/apiClient.js';

export interface UsePaymentsArgs {
  from?: string;
  to?: string;
}

export const PAYMENTS_QUERY_KEY = 'payments' as const;

function withinRange(payment: PaymentTransaction, from: string | undefined, to: string | undefined): boolean {
  // Match against `paid_on` if present, else `due_on`. If neither is set,
  // the transaction is included regardless of the filter.
  const date = payment.paid_on ?? payment.due_on;
  if (date === null || date === undefined) return true;
  if (from !== undefined && date < from) return false;
  if (to !== undefined && date > to) return false;
  return true;
}

export function usePayments(args: UsePaymentsArgs = {}): {
  query: UseQueryResult<PaymentTransactionList, Error>;
  filtered: readonly PaymentTransaction[];
} {
  const query = useQuery<PaymentTransactionList, Error>({
    queryKey: [PAYMENTS_QUERY_KEY],
    queryFn: () => apiClient.payments.listTransactions(),
  });

  const filtered = React.useMemo<readonly PaymentTransaction[]>(() => {
    if (!query.isSuccess) return [];
    return query.data.filter((p) => withinRange(p, args.from, args.to));
  }, [query.isSuccess, query.data, args.from, args.to]);

  return { query, filtered };
}
